import { List } from '@react-admin/ra-rbac';
import {
  AutocompleteArrayInput,
  BooleanField,
  BulkUpdateButton,
  DatagridConfigurable,
  FunctionField,
  ImageField,
  NullableBooleanInput,
  NumberField,
  NumberInput,
  ReferenceArrayInput,
  ReferenceField,
  SelectField,
  SimpleForm,
  TextArrayInput,
  TextField,
  TextInput,
  UrlField,
  useCanAccess,
} from 'react-admin';
import { BulkUpdateFormButton } from '@react-admin/ra-form-layout';
import { Check, Clear } from '@mui/icons-material';
import TzDateField from '../../Components/TzDate/TzDateField';
import { IMAGE_STATUS_CHOICES } from './utils';
import TzDateTimeInput from '../../Components/TzDate/TzDateTimeInput';
import { OfferResource } from '../../Entities/Offer';
import {
  createFilterQuery,
  DEFAULT_LIST_PAGE_IMAGE_MAX_HEIGHT,
  DEFAULT_LIST_PAGE_IMAGE_MAX_WIDTH,
  DEFAULT_LIST_PAGE_PAGINATION_LIMIT,
} from '../../utils';
import TruncatedTextField from '../../Components/TruncatedTextField';
import LargePagination from '../../Components/LargePagination';
import { ReactElement } from 'react';
import JsonApiHasOneReferenceInput from '../../Components/JsonApiHasOneReferenceInput';

const filters = (
  canListCategories: boolean,
  canListMerchants: boolean,
  canListProducts: boolean,
  canListRegions: boolean,
  canListManufacturers: boolean,
): ReactElement[] =>
  [
    <TextInput key="id" label="ID" source="id" alwaysOn />,
    <TextArrayInput key="name" label="Name contains..." source="sqlContains.name" alwaysOn />,
    <TextArrayInput
      key="not_name"
      label="Name does not contain..."
      source="not.sqlContains.name"
    />,
    canListManufacturers && (
      <ReferenceArrayInput
        key="filter.manufacturer"
        label="Manufacturer"
        source="equals.manufacturer.id"
        reference="manufacturers"
      >
        <AutocompleteArrayInput
          label="Manufacturer"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListMerchants && (
      <ReferenceArrayInput
        key="merchant_status"
        label="Merchant Status"
        source="any.merchant.merchant_status.id"
        reference="merchant_statuses"
        alwaysOn
      >
        <AutocompleteArrayInput label="Merchant Status" optionText="attributes.name" />
      </ReferenceArrayInput>
    ),
    canListMerchants && canListRegions && (
      <ReferenceArrayInput
        key="merchant_region"
        label="Merchant Region"
        source="any.merchant.region.id"
        reference="regions"
        alwaysOn
      >
        <AutocompleteArrayInput label="Merchant Region" optionText="attributes.name" />
      </ReferenceArrayInput>
    ),
    canListMerchants && (
      <ReferenceArrayInput
        key="merchants"
        label="Merchants"
        source="any.merchant.id"
        reference="merchants"
      >
        <AutocompleteArrayInput
          label="Merchants"
          optionText="attributes.merchant_name"
          filterToQuery={createFilterQuery(['merchant_name'])}
        />
      </ReferenceArrayInput>
    ),
    canListMerchants && (
      <ReferenceArrayInput
        key="excluded_merchants"
        label="Exclude Merchants"
        source="not.any.merchant.id"
        reference="merchants"
      >
        <AutocompleteArrayInput
          label="Exclude Merchants"
          optionText="attributes.merchant_name"
          filterToQuery={createFilterQuery(['merchant_name'])}
        />
      </ReferenceArrayInput>
    ),
    canListProducts && (
      <ReferenceArrayInput
        key="products"
        label="Products"
        source="any.product.id"
        reference="products"
      >
        <AutocompleteArrayInput
          label="Products"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListProducts && (
      <NullableBooleanInput key="has_product" label="Has Product?" source="not.isNull.product" />
    ),
    canListCategories && (
      <ReferenceArrayInput
        key="categories"
        label="Categories"
        source="any.category.id"
        reference="categories"
      >
        <AutocompleteArrayInput
          label="Categories"
          optionText="attributes.full_name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListCategories && (
      <NullableBooleanInput key="has_category" label="Has Category?" source="not.isNull.category" />
    ),
    canListCategories && (
      <NullableBooleanInput
        key="is_compareable"
        label="Is Compareable?"
        source="equals.category.is_offer_comparison_allowed"
      />
    ),
    <TextArrayInput key="model" label="Model" source="sqlContains.model" />,
    <TextArrayInput key="description" label="Description" source="sqlContains.description" />,
    <TextInput key="mpn" label="MPN" source="contains.mpn" />,
    <TextInput key="gtin" label="GTIN" source="gtin" />,
    <TextInput
      key="external_category"
      label="Category (From Feed) Equals"
      source="equals.external_category"
    />,
    <TextArrayInput
      key="external_category_contains"
      label="Category (From Feed) Contains"
      source="sqlContains.external_category"
    />,
    <TextArrayInput
      key="external_category_not_contains"
      label="Category (From Feed) Does Not Contain"
      source="not.sqlContains.external_category"
    />,
    <TextInput
      key="external_manufacturer"
      label="Manufacturer (From Feed)"
      source="contains.external_manufacturer"
    />,
    <TextInput key="site_id" label="Site ID" source="site_id" />,
    <NullableBooleanInput key="is_in_stock" label="In Stock?" source="is_in_stock" />,
    <NumberInput
      key="current_price_lt"
      label="Current Price Less Than"
      source="lessThan.current_price"
    />,
    <NumberInput
      key="current_price_gt"
      label="Current Price Greater Than"
      source="greaterThan.current_price"
    />,
    <NumberInput
      key="normal_price_lt"
      label="Normal Price Less Than"
      source="lessThan.normal_price"
    />,
    <NumberInput
      key="normal_price_gt"
      label="Normal Price Greater Than"
      source="greaterThan.normal_price"
    />,
    <NumberInput
      key="shipping_cost_lt"
      label="Shipping Cost Less Than"
      source="lessThan.shipping_cost"
    />,
    <NumberInput
      key="shipping_cost_gt"
      label="Shipping Cost Greater Than"
      source="greaterThan.shipping_cost"
    />,
    <TextInput key="url" label="URL" source="contains.raw_url" />,
    <TzDateTimeInput key="starts_at_before" label="Starts Before" source="lessThan.starts_at" />,
    <TzDateTimeInput key="starts_at_after" label="Starts After" source="greaterThan.starts_at" />,
    <TzDateTimeInput key="ends_at" label="Ends After" source="greaterThan.ends_at" />,
    <TzDateTimeInput key="ends_at" label="Ends Before" source="lessThan.ends_at" />,
    <NullableBooleanInput key="is_approved" label="Approved?" source="is_approved" />,
    <NullableBooleanInput key="is_adult" label="For Adult Audiences?" source="is_adult" />,
    <NullableBooleanInput
      key="is_auto_categorized"
      label="Is Auto/AI Categorized?"
      source="is_auto_categorized"
    />,
    <TzDateTimeInput key="created_at_before" label="Created Before" source="lessThan.created_at" />,
    <TzDateTimeInput
      key="created_at_after"
      label="Created After"
      source="greaterThan.created_at"
    />,
    <TzDateTimeInput key="edited_at_before" label="Edited Before" source="lessThan.edited_at" />,
    <TzDateTimeInput key="edited_at_after" label="Edited After" source="greaterThan.edited_at" />,
  ].filter((value) => typeof value !== 'boolean');

function OfferBulkActionButtons({
  canListCategories,
  canListProducts,
  canWriteOffers,
}: {
  canListCategories: boolean;
  canListProducts: boolean;
  canWriteOffers: boolean;
}): ReactElement {
  return (
    <>
      {canListProducts && canWriteOffers && (
        <BulkUpdateButton
          label="Clear Product"
          icon={<Clear />}
          data={{ relationships: { product: { data: null } } }}
        />
      )}
      {canListCategories && canWriteOffers && (
        <BulkUpdateButton
          label="Clear Category"
          icon={<Clear />}
          data={{ relationships: { category: { data: null } } }}
        />
      )}
      {canWriteOffers && (
        <BulkUpdateButton
          label="Approve"
          icon={<Check />}
          data={{ attributes: { is_approved: true } }}
        />
      )}
      {canWriteOffers && (
        <BulkUpdateFormButton>
          <SimpleForm>
            {canListProducts && (
              <JsonApiHasOneReferenceInput<OfferResource, 'product'>
                label="Product"
                nullable={false}
                optionText="attributes.name"
                reference="products"
                source="relationships.product"
              />
            )}
            {canListCategories && (
              <JsonApiHasOneReferenceInput<OfferResource, 'category', 'categories'>
                label="Category"
                nullable={false}
                optionText="attributes.full_name"
                reference="categories"
                source="relationships.category"
              />
            )}
            <NullableBooleanInput source="attributes.is_approved" label="Approved?" />
            <NullableBooleanInput source="attributes.is_adult" label="For Adult Audiences?" />
          </SimpleForm>
        </BulkUpdateFormButton>
      )}
    </>
  );
}

export default function OfferList(): ReactElement {
  const categoryAccess = useCanAccess({ resource: 'categories', action: 'list' });
  const merchantAccess = useCanAccess({ resource: 'merchants', action: 'list' });
  const productAccess = useCanAccess({ resource: 'products', action: 'list' });
  const regionAccess = useCanAccess({ resource: 'regions', action: 'list' });
  const shopScansAccess = useCanAccess({ resource: 'scans', action: 'list' });
  const offerAccess = useCanAccess({ resource: 'offers', action: 'write' });
  const manufacturerAccess = useCanAccess({ resource: 'manufacturers', action: 'list' });

  const canListCategories = categoryAccess.canAccess ?? false;
  const canListMerchants = merchantAccess.canAccess ?? false;
  const canListProducts = productAccess.canAccess ?? false;
  const canListRegions = regionAccess.canAccess ?? false;
  const canListShopScans = shopScansAccess.canAccess ?? false;
  const canWriteOffers = offerAccess.canAccess ?? false;
  const canListManufacturers = manufacturerAccess.canAccess ?? false;

  const filterDefaultValues: Record<
    string,
    Record<string, Record<string, Record<string, string>>>
  > = {
    // Default filters: Live merchants in Australia
    any: {},
  };

  if (canListMerchants) {
    filterDefaultValues.any = {
      ...filterDefaultValues.any,
      merchant: { merchant_status: { id: '1' } },
    };

    if (canListRegions) {
      filterDefaultValues.any.merchant = {
        ...filterDefaultValues.any.merchant,
        region: { id: '1' },
      };
    }
  }

  const prefetch: string[] = [];
  if (canListCategories) prefetch.push('category');
  if (canListMerchants) prefetch.push('merchant');
  if (canListProducts) prefetch.push('product');
  if (canListShopScans) prefetch.push('last_scan');
  if (canListManufacturers) prefetch.push('manufacturer');

  return (
    <List
      filters={filters(
        canListCategories,
        canListMerchants,
        canListProducts,
        canListRegions,
        canListManufacturers,
      )}
      perPage={DEFAULT_LIST_PAGE_PAGINATION_LIMIT}
      pagination={<LargePagination />}
      queryOptions={{ meta: { prefetch } }}
      sort={{ field: 'id', order: 'DESC' }}
      filterDefaultValues={filterDefaultValues}
    >
      <DatagridConfigurable
        bulkActionButtons={
          <OfferBulkActionButtons
            canListCategories={canListCategories}
            canListProducts={canListProducts}
            canWriteOffers={canWriteOffers}
          />
        }
        omit={[
          'attributes.created_at',
          'attributes.currency',
          'attributes.description',
          'attributes.edited_at',
          'attributes.ends_at',
          'attributes.external_category',
          'attributes.external_image_last_download_attempts',
          'attributes.external_image_last_downloaded_at',
          'attributes.external_image_url',
          'attributes.external_manufacturer',
          'attributes.gtin',
          'attributes.image_status',
          'attributes.is_adult',
          'attributes.is_auto_categorized',
          'attributes.merchant_name',
          'attributes.model',
          'attributes.mpn',
          'attributes.normal_price',
          'attributes.url',
          'attributes.shipping_cost',
          'attributes.site_id',
          'attributes.starts_at',
        ]}
      >
        <TextField label="ID" source="id" />
        <ImageField
          label="Image"
          sortable={false}
          source="attributes.image_url"
          sx={{
            '& img': {
              maxHeight: DEFAULT_LIST_PAGE_IMAGE_MAX_HEIGHT,
              maxWidth: DEFAULT_LIST_PAGE_IMAGE_MAX_WIDTH,
            },
          }}
        />
        <ImageField
          label="Image (From Feed)"
          sortable={false}
          source="attributes.external_image_url"
        />
        {canListManufacturers && (
          <ReferenceField
            emptyText="Not assigned"
            label="Manufacturer"
            source="relationships.manufacturer.data.id"
            reference="manufacturers"
          >
            <TextField source="attributes.name" />
          </ReferenceField>
        )}
        <TruncatedTextField length={40} label="Name" source="attributes.name" />
        {canListMerchants && (
          <ReferenceField
            label="Merchant"
            source="relationships.merchant.data.id"
            reference="merchants"
          >
            <TextField source="attributes.merchant_name" />
          </ReferenceField>
        )}
        {canListProducts && (
          <ReferenceField
            emptyText="Not assigned"
            label="Product"
            source="relationships.product.data.id"
            reference="products"
          >
            <TextField source="attributes.name" />
          </ReferenceField>
        )}
        {canListCategories && (
          <ReferenceField
            emptyText="Not assigned"
            label="Category"
            source="relationships.category.data.id"
            reference="categories"
          >
            <TextField source="attributes.full_name" />
          </ReferenceField>
        )}
        <TextField label="Model" source="attributes.model" />
        <TextField label="Description" source="attributes.description" />
        <TextField label="MPN" source="attributes.mpn" />
        <TextField label="GTIN" source="attributes.gtin" />
        <TextField label="Category (From Feed)" source="attributes.external_category" />
        <TextField label="Manufacturer (From Feed)" source="attributes.external_manufacturer" />
        <BooleanField label="In Stock?" source="attributes.is_in_stock" />
        <TextField label="Site ID" source="attributes.site_id" />
        <FunctionField
          label="Current Price"
          source="attributes.current_price"
          render={(record?: Partial<OfferResource>, source?: string) =>
            record &&
            source && (
              <NumberField
                source={source}
                options={{ style: 'currency', currency: record?.attributes?.currency ?? 'AUD' }}
              />
            )
          }
        />
        <FunctionField
          label="Normal Price"
          source="attributes.normal_price"
          render={(record?: Partial<OfferResource>, source?: string) =>
            record &&
            source && (
              <NumberField
                source={source}
                options={{ style: 'currency', currency: record?.attributes?.currency ?? 'AUD' }}
              />
            )
          }
        />
        <FunctionField
          label="Shipping Cost"
          source="attributes.shipping_cost"
          render={(record?: Partial<OfferResource>, source?: string) =>
            record &&
            source && (
              <NumberField
                source={source}
                options={{ style: 'currency', currency: record?.attributes?.currency ?? 'AUD' }}
              />
            )
          }
        />
        <TextField label="Currency" source="attributes.currency" />
        <UrlField label="URL" source="attributes.url" />
        <TzDateField
          label="Image (From Feed) / Last Downloaded At"
          source="attributes.external_image_last_downloaded_at"
          showTime
        />
        <TextField
          label="Image (From Feed) / Last Download Attempt Count"
          source="attributes.external_image_last_download_attempts"
        />
        <SelectField
          label="Image (From Feed) / Image Download Status"
          source="attributes.image_status"
          choices={IMAGE_STATUS_CHOICES}
        />
        <TzDateField label="Start Time" source="attributes.starts_at" showTime />
        <TzDateField label="End Time" source="attributes.ends_at" showTime />
        <BooleanField label="Approved?" source="attributes.is_approved" />
        <BooleanField label="Adult?" source="attributes.is_adult" />
        <BooleanField label="Auto Categorized?" source="attributes.is_auto_categorized" />
        {canListShopScans && (
          <ReferenceField
            label="Last Scan Time"
            source="relationships.last_scan.data.id"
            reference="scans"
          >
            <TzDateField source="attributes.end_date" showTime />
          </ReferenceField>
        )}
        <TzDateField label="Created At" source="attributes.created_at" showTime />
        <TzDateField label="Edited At" source="attributes.edited_at" showTime />
      </DatagridConfigurable>
    </List>
  );
}
